import { addDays, format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DashboardPanel } from "@/components/building/DashboardPanel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatBillingDate } from "../format";
import { useBillingMoney, useRemoveBillingBuilding } from "../hooks";
import { billingErrorMessage } from "../messages";
import type { BilledBuilding } from "../types";

interface RemoveBuildingDialogProps {
  building: BilledBuilding | null;
  orgId: string;
  periodEnd: string;
  currency: string;
  onOpenChange: (open: boolean) => void;
}

function RemovalHeading({ buildingName, periodEnd }: { buildingName: string; periodEnd: string }) {
  const { t } = useLanguage();
  return (
    <div className="relative space-y-1.5 pl-5">
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
      <h3 className="flex min-w-0 items-baseline gap-2 whitespace-nowrap text-base leading-6 tracking-tight">
        <span
          className="min-w-0 truncate text-base font-medium leading-6 tracking-tight"
          title={buildingName}
        >
          {buildingName}
        </span>
        <span className="shrink-0 text-base font-medium leading-6 tracking-tight">
          {fill(t.billing.removeDialog.activeUntil, { date: formatBillingDate(periodEnd) })}
        </span>
      </h3>
    </div>
  );
}

function RemovalEffects({
  building,
  periodEnd,
  deletesOn,
  currency,
}: {
  building: BilledBuilding;
  periodEnd: string;
  deletesOn: string;
  currency: string;
}) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const copy = t.billing.removeDialog;
  const nextPeriodStart = formatBillingDate(format(addDays(parseISO(periodEnd), 1), "yyyy-MM-dd"));

  return (
    <div className="space-y-4 text-sm">
      <section className="pb-2">
        <RemovalHeading buildingName={building.building_name} periodEnd={periodEnd} />
        <p className="mt-3 text-xs text-muted-foreground">
          {fill(building.renewal_rate_cents === undefined ? copy.bodyWithoutAmount : copy.body, {
            date: nextPeriodStart,
            amount: money(building.renewal_rate_cents ?? 0, currency),
          })}
        </p>
      </section>
      <section>
        <DashboardPanel>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {fill(copy.afterHeading, { date: formatBillingDate(periodEnd) })}
          </h3>
          <p className="mt-2 text-muted-foreground">{fill(copy.afterBody, { date: deletesOn })}</p>
          {/* The dialog projects both dates from the period the overview shows;
              the service row takes them from the mirrored subscription period.
              They agree while the day-of-month anchor holds, so say the dates
              are confirmed on removal rather than presenting a projection as
              settled. */}
          <p className="mt-2 text-xs text-muted-foreground">{copy.datesNote}</p>
        </DashboardPanel>
      </section>
      <p className="text-xs leading-relaxed text-muted-foreground">{copy.transferHint}</p>
    </div>
  );
}

export function RemoveBuildingDialog({
  building,
  orgId,
  periodEnd,
  currency,
  onOpenChange,
}: RemoveBuildingDialogProps) {
  const { t } = useLanguage();
  const mutation = useRemoveBillingBuilding();
  const copy = t.billing.removeDialog;
  const deletesOn = format(addDays(parseISO(periodEnd), 30), "dd.MM.yyyy");

  const handleRemove = async () => {
    if (!building) return;
    try {
      await mutation.mutateAsync({ orgId, buildingId: building.building_id });
      toast.success(copy.removed);
      onOpenChange(false);
    } catch (error) {
      toast.error(billingErrorMessage(error, t, copy.removeFailed));
    }
  };

  return (
    <Dialog open={Boolean(building)} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader className="mb-2">
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        {building && (
          <RemovalEffects
            building={building}
            periodEnd={periodEnd}
            deletesOn={deletesOn}
            currency={currency}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {copy.cancel}
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
